import { FormControl, Grid } from "@mui/material";
import { ButtonCancel } from "components/buttons";
import { ButtonApplyFilter } from "components/buttons/ButtonApplyFilter";
import { MprcInputStyles } from "components/filter/MprcInputStyles";
import { AutoCompleteDataModel, AutoCompleteSuggestions, MprcAutoComplete } from "components/MprcAutoComplete";
import { EnrollStatus } from "generated/CoreApi_v1";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IAdvFilterPickerProps } from "views/general/AdvancedFilter/interfaces";

export interface EnrollStatusItem {
    text: string;
    value: EnrollStatus;
}

////////////////////////////////////////////////////////////////////////////////

export const EnrollStatusPicker: React.FC<IAdvFilterPickerProps> = (props) => {

    const { filter, onApply, onCancel } = props;

    const { t } = useTranslation("shared");

    const [selected, setSelected] = useState<AutoCompleteDataModel[]>([]);

    const inputRef = useRef<any>(undefined);
    const multiSelect = filter.canRepeat ?? false;

    const items = useRef<EnrollStatusItem[]>([
        { text: t("Enrollment.Status.NotStartedText", { ns: "lms" }), value: EnrollStatus.NotStarted },
        { text: t("Enrollment.Status.InProgressText", { ns: "lms" }), value: EnrollStatus.InProgress },
        { text: t("Enrollment.Status.WaitingScoreText", { ns: "lms" }), value: EnrollStatus.WaitingScore },
        { text: t("Enrollment.Status.ApprovedText", { ns: "lms" }), value: EnrollStatus.Approved },
        { text: t("Enrollment.Status.ReprovedText", { ns: "lms" }), value: EnrollStatus.Reproved },
        { text: t("Enrollment.Status.InvalidText", { ns: "lms" }), value: EnrollStatus.Lapsed },
        { text: t("Enrollment.Status.CanceledText", { ns: "lms" }), value: EnrollStatus.Canceled },
        { text: t("Enrollment.Status.RevokedText", { ns: "lms" }), value: EnrollStatus.Revoked },
    ]);

    ////////////////////////////////////////////////////////////////////////////////

    ///
    const getSuggestions = (searchText: string, pageNumber: number, pageSize: number): Promise<AutoCompleteSuggestions<AutoCompleteDataModel>> => {
        searchText = searchText.toLowerCase();
        return new Promise<AutoCompleteSuggestions<AutoCompleteDataModel>>((resolve) => {
            const filterItems = searchText.length > 0
                ? items.current.filter(opt => opt.text.toLowerCase().includes(searchText))
                : [...items.current];

            resolve({
                items: filterItems.map(x => ({
                    key: x.value,
                    label: x.text,
                    canDelete: true,
                })) as AutoCompleteDataModel[],
                totalRows: filterItems.length
            });
        });
    };

    ///
    const handleApply = () => {
        if (selected.length > 0) {
            const newFilter = selected.map(item => ({
                command: filter!.command,
                optionalGroup: filter?.optionGroup,
                text: `${filter?.displayName.replace("...", "")}: ${item.label}`,
                value: item.key,
                quickFilter: null,
                period: null,
                range: null,
                includeDescendants: null
            }));
            onApply(newFilter, filter);
        }
    };

    ///
    useEffect(() => {
        (inputRef as any)?.current?.focus();
    }, [inputRef]);

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    return (
        <Grid container alignItems="flex-end" spacing={2}>
            <Grid size="grow">
                <FormControl fullWidth>
                    <MprcAutoComplete
                        label={filter.placeholder ?? t(`AdvFilter.Picker.EnrollStatus.Placeholder${multiSelect ? "_multi" : ""}`)}
                        fullWidth
                        autoFocus
                        multiSelect={multiSelect}
                        inputRef={inputRef}
                        selectedItems={selected}
                        onChange={(items) => setSelected(items)}
                        onGetSuggestions={getSuggestions}
                        onApply={handleApply}
                        onCancel={onCancel}
                        sx={MprcInputStyles.filter}
                    />
                </FormControl>
            </Grid>
            <Grid>
                <ButtonApplyFilter
                    variant="text"
                    disabled={selected.length == 0}
                    onClick={handleApply}
                />
                <ButtonCancel
                    variant="text"
                    onClick={onCancel}
                />
            </Grid>
        </Grid>
    );
};
